<script lang="ts">
  import { servers } from '$lib/store'
  import { goto } from "@mateothegreat/svelte5-router"
  import { describeSeries, progressSeries, fetchSeriesDescriptionEn } from '$lib/apis/series/api'
  import { markCompleted, markUncompleted } from '$lib/apis/read/api'
  import { seriesDataEpoch } from '$lib/operations.svelte'
  import SeriesInfos from '$lib/components/nuread/SeriesInfos.svelte'
  import VolChapSlot from '$lib/components/nuread/VolChapSlot.svelte'
  import Play from '@lucide/svelte/icons/play'
  import Star from '@lucide/svelte/icons/star'
  import Tags from '@lucide/svelte/icons/tags'
  import Hash from '@lucide/svelte/icons/hash'
  import Options from '@lucide/svelte/icons/ellipsis'
  import ChevronsUpDown from '@lucide/svelte/icons/chevrons-up-down'
  import ChevronsDownUp from '@lucide/svelte/icons/chevrons-down-up'
  import ChevronDown from '@lucide/svelte/icons/chevron-down'
  import BookCheck from '@lucide/svelte/icons/book-check'
  import BookX from '@lucide/svelte/icons/book-x'
  import X from '@lucide/svelte/icons/x'
  import { Button } from '$lib/components/ui/button/index'
  import { Badge } from '$lib/components/ui/badge/index.js'
  import * as Card from '$lib/components/ui/card/index.js'
  import { VList } from 'virtua/svelte'
  import { mode } from 'mode-watcher'
  import { cn, lineClampStyle, linesThatFit, bookVariantPrefKey } from '$lib/utils'
  import { formatMonaidDescription, containsJapanese } from '$lib/monaid'
  import { tick, untrack } from 'svelte'
  import PLACEHOLDER from '$assets/icons/book/cover_placeholder.svg'

  let { route } = $props()
  const serverid = $derived(route.result.path.params.mediaid)
  const libraryid = $derived(route.result.path.params.libraryid)
  const seriesid = $derived(route.result.path.params.seriesid)

  let cdnUrl = $derived($servers[serverid].urls[0])

  let coverSrc = $state(PLACEHOLDER)
  let bannerSrc = $state('')
  let seriesRes = $state({})
  let progRes = $state({})
  let currentTab: 'volume' | 'chapter' | 'reviews' = $state('volume')
  let expandedReview: number | null = $state(null)
  let headerExpanded = $state(false)
  let headerShellEl: HTMLElement | undefined = $state()
  let headerMetaEl: HTMLElement | undefined = $state()
  let headerMetaHeight = $state(0)
  let headerActionsEl: HTMLElement | undefined = $state()
  let headerActionsBottom = $state(0) // px from shell top → actions bottom
  let headerTitleEl: HTMLElement | undefined = $state()
  let headerDescEl: HTMLElement | undefined = $state()
  let headerTagsEl: HTMLElement | undefined = $state()
  let headerNeedsExpand = $state(false)
  let headerDescClamp = $state(0)
  let selectedIds = $state(new Set<string>())
  let selectionAnchorId = $state<string | null>(null)
  let markingBusy = $state(false)

  let series = $derived(seriesRes.Series ?? {})
  let progress = $derived(progRes.Progress ?? [])
  let reviews = $derived(series?.Reviews ?? [])
  const selectionActive = $derived(selectedIds.size > 0)

  function slotVariants(book: any): any[] {
    return Array.isArray(book?.variants) && book.variants.length > 0
      ? book.variants
      : [book]
  }

  function isSlotSelected(book: any): boolean {
    return slotVariants(book)
      .map((v: any) => v?.id)
      .filter(Boolean)
      .some((id: string) => selectedIds.has(id))
  }

  /** Preferred variant id for a grouped slot (matches VolChapSlot). */
  function preferredBookId(book: any): string {
    const variants = slotVariants(book)
    const type = book?.type
    const num = type == 'volume' ? book.volume : book.chapter
    const subNum = type == 'volume' ? book.subVolume : (book.subChapter ?? 0)
    const prefKey = bookVariantPrefKey(seriesid, type, num, subNum ?? 0)
    let pref = ''
    try {
      pref = localStorage.getItem(prefKey) ?? ''
    } catch {
      /* ignore */
    }
    const ids = variants.map((v: any) => v?.id).filter(Boolean)
    return pref && ids.includes(pref) ? pref : (book?.id ?? ids[0] ?? '')
  }

  function findSlotIndex(list: any[], bookId: string): number {
    return list.findIndex((b) =>
      slotVariants(b).some((v: any) => v?.id === bookId)
    )
  }

  function toggleBookSelect(bookId: string, opts?: { shiftKey?: boolean }) {
    const tab = currentTab === 'reviews' ? 'volume' : currentTab
    const list: any[] = books[tab] ?? []

    if (opts?.shiftKey && selectionAnchorId) {
      const a = findSlotIndex(list, selectionAnchorId)
      const b = findSlotIndex(list, bookId)
      if (a >= 0 && b >= 0) {
        const lo = Math.min(a, b)
        const hi = Math.max(a, b)
        // Match the toggle intent of the end slot for the whole range.
        const select = !isSlotSelected(list[b])
        const next = new Set(selectedIds)
        for (let i = lo; i <= hi; i++) {
          const id = preferredBookId(list[i])
          if (!id) continue
          if (select) next.add(id)
          else {
            for (const v of slotVariants(list[i])) {
              if (v?.id) next.delete(v.id)
            }
          }
        }
        selectedIds = next
        return
      }
    }

    const next = new Set(selectedIds)
    if (next.has(bookId)) next.delete(bookId)
    else next.add(bookId)
    selectedIds = next
    selectionAnchorId = bookId
  }

  function clearSelection() {
    selectedIds = new Set()
    selectionAnchorId = null
  }

  async function markSelection(complete: boolean) {
    const server = $servers[serverid]
    const ids = [...selectedIds]
    if (!server || !seriesid || ids.length === 0 || markingBusy) return
    markingBusy = true
    try {
      const fn = complete ? markCompleted : markUncompleted
      await Promise.all(ids.map((id) => fn(server, seriesid, id)))
      progRes = (await progressSeries(server, seriesid)) ?? progRes
      clearSelection()
    } finally {
      markingBusy = false
    }
  }

  /** JP = stored Infos.description (ranobedb); EN = monaid translation. */
  let descLang: 'jp' | 'en' = $state('jp')
  let enDescription = $state('')
  let enDescLoading = $state(false)
  let enDescError = $state(false)

  const jpDescription = $derived(series.Infos?.description ?? '')
  const ranobedbId = $derived(
    series?.MediaIds?.ranobedb ? String(series.MediaIds.ranobedb) : ''
  )
  const descIsJapanese = $derived(containsJapanese(jpDescription))
  const canSwitchDesc = $derived(!!ranobedbId && descIsJapanese)
  const displayDescription = $derived(
    descLang === 'en' && enDescription ? enDescription : jpDescription
  )

  let activeTabHeaderClass = 'underline underline-offset-7 font-light'
  let tabHeaderClass = 'font-medium'

  const HEADER_MIN_REM = 17.8125 // floor while layout is measuring
  const HEADER_MD_OVERLAP_REM = 10 // md:-mt-40
  /** Volumes/Chapters/Reviews row (`bottom-2` + label line). */
  const HEADER_TABS_REM = 2.5
  /** Gap between Continue/actions cluster and the tab row. */
  const HEADER_ACTIONS_GAP_REM = 0.75

  function rootFontPx(): number {
    if (typeof document === 'undefined') return 16
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  }

  function isClipped(el?: HTMLElement) {
    if (!el) return false
    const { scrollHeight, clientHeight } = el
    // Not laid out yet — don't treat as clipped or the button sticks on.
    if (clientHeight === 0) return false
    return scrollHeight > clientHeight + 2
  }

  function syncHeaderNeedsExpand() {
    if (headerExpanded) return
    const genresClipped = (series?.genres?.length ?? 0) > 8
    headerNeedsExpand =
      genresClipped ||
      isClipped(headerTitleEl) ||
      isClipped(headerDescEl) ||
      isClipped(headerTagsEl)
  }

  function syncHeaderMetrics() {
    const root = rootFontPx()
    const shellTop = headerShellEl?.getBoundingClientRect().top ?? 0
    if (headerMetaEl) {
      headerMetaHeight = headerMetaEl.getBoundingClientRect().height
    }
    if (headerActionsEl && headerShellEl) {
      headerActionsBottom =
        headerActionsEl.getBoundingClientRect().bottom - shellTop
    }
    syncHeaderNeedsExpand()
  }

  $effect(() => {
    const targets = [
      headerShellEl,
      headerMetaEl,
      headerActionsEl,
      headerTitleEl,
      headerDescEl,
      headerTagsEl,
    ].filter((el): el is HTMLElement => !!el)
    if (targets.length === 0) return

    const ro = new ResizeObserver(() => syncHeaderMetrics())
    for (const el of targets) ro.observe(el)
    syncHeaderMetrics()
    return () => ro.disconnect()
  })

  $effect(() => {
    void series
    void headerExpanded
    // Wait for DOM + layout so line-clamp measurements are accurate.
    tick().then(() => {
      requestAnimationFrame(syncHeaderMetrics)
    })
  })

  // Collapsed: max-height budgets the box; measured line-clamp adds the ellipsis.
  $effect(() => {
    void series
    void displayDescription
    void descLang
    if (headerExpanded || !headerDescEl) {
      headerDescClamp = 0
      return
    }
    const el = headerDescEl
    const sync = () => {
      const maxH = parseFloat(getComputedStyle(el).maxHeight)
      const budget = Number.isFinite(maxH) && maxH > 0 ? maxH : el.clientHeight
      headerDescClamp = linesThatFit(el, budget)
      syncHeaderNeedsExpand()
    }
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    tick().then(() => requestAnimationFrame(sync))
    return () => ro.disconnect()
  })

  // Shell must clear: meta content, Continue/actions under the cover, and the tab row.
  const headerHeightRem = $derived.by(() => {
    const root = rootFontPx()
    const overlapRem =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches
        ? HEADER_MD_OVERLAP_REM
        : 0
    const metaRem = headerMetaHeight / root
    const fromMeta = metaRem - overlapRem + HEADER_TABS_REM
    const fromActions =
      headerActionsBottom / root + HEADER_ACTIONS_GAP_REM + HEADER_TABS_REM
    return Math.max(HEADER_MIN_REM, fromMeta, fromActions)
  })

  const showHeaderExpand = $derived(headerExpanded || headerNeedsExpand)

  $effect(() => {
    void seriesDataEpoch.value
    describeSeries($servers[serverid], seriesid).then(r => (seriesRes = r))
    progressSeries($servers[serverid], seriesid).then(r => (progRes = r))
  })

  // Reset / load EN description when series or ranobedb id changes.
  $effect(() => {
    const sid = seriesid
    const server = $servers[serverid]
    descLang = 'jp'
    enDescription = ''
    enDescError = false
    enDescLoading = false

    // Avoid fetching against a stale series payload while navigating.
    if (series?.id && series.id !== sid) return

    const id = ranobedbId
    if (!id || !descIsJapanese || !server) return

    let cancelled = false
    enDescLoading = true
    fetchSeriesDescriptionEn(server, sid)
      .then((text) => {
        if (cancelled) return
        enDescription = formatMonaidDescription(text)
        enDescError = !text
      })
      .catch((err) => {
        if (cancelled) return
        console.log(err)
        enDescError = true
        enDescription = ''
      })
      .finally(() => {
        if (!cancelled) enDescLoading = false
      })
    return () => {
      cancelled = true
    }
  })

  let maxWidth = $state(0)
  let itemWidth = $state(110)
  let itemHeight = $state(110 * 1.446)
  let columns = $state(2)
  let listHostEl: HTMLElement | undefined = $state()
  let winW = $state(typeof window !== 'undefined' ? window.innerWidth : 0)

  const COVER_ASPECT = 1.446
  /** Min cover height in rem (was hard-coded 240px @ 16px root). */
  const MIN_COVER_REM = 15
  /** Title line under the cover. */
  const TITLE_BAND_REM = 1.75
  /** Wider gap on narrow screens so covers don't feel cramped. */
  const gridGapRem = $derived(winW > 0 && winW < 450 ? 1.5 : 1)

  /** Collapse consecutive same volume+subVolume into one slot with a `variants` list. */
  function groupVolumesSorted(books: any[]) {
    const groups: any[] = []
    for (const e of books) {
      const prev = groups[groups.length - 1]
      if (
        prev &&
        prev.volume === e.volume &&
        (prev.subVolume ?? null) === (e.subVolume ?? null)
      ) {
        prev.variants.push(e)
        continue
      }
      groups.push({ ...e, variants: [e] })
    }
    return groups
  }

  /** Collapse consecutive same chapter+subChapter into one slot with a `variants` list. */
  function groupChaptersSorted(books: any[]) {
    const groups: any[] = []
    for (const e of books) {
      const prev = groups[groups.length - 1]
      if (
        prev &&
        prev.chapter === e.chapter &&
        (prev.subChapter ?? null) === (e.subChapter ?? null)
      ) {
        prev.variants.push(e)
        continue
      }
      groups.push({ ...e, variants: [e] })
    }
    return groups
  }

  function attachReadingProgress(books: any[], progList: any[]) {
    return books.map((b: any) => {
      const variants = (b.variants ?? [b]).map((v: any) => {
        const prog = progList.find((p: any) => p.bookID == v.id)
        return {
          ...v,
          progression: prog?.Current,
          isCompleted: prog?.IsCompleted,
        }
      })
      const primary = variants[0] ?? b
      return {
        ...primary,
        variants,
        progression: primary.progression,
        isCompleted: primary.isCompleted,
      }
    })
  }

  let books = $derived.by(() => {
    const raw = series?.Books ?? []

    const volumes = attachReadingProgress(
      groupVolumesSorted(
        raw
          .filter((e: any) => e.type === 'volume')
          .sort((a: any, b: any) =>
            a.volume - b.volume || (a.subVolume ?? 0) - (b.subVolume ?? 0)
          )
      ),
      progress
    )

    const chapters = attachReadingProgress(
      groupChaptersSorted(
        raw
          .filter((e: any) => e.type === 'chapter')
          .sort((a: any, b: any) =>
            a.chapter - b.chapter ||
            (a.subChapter ?? 0) - (b.subChapter ?? 0)
          )
      ),
      progress
    )

    return { volume: volumes, chapter: chapters }
  })
  function toggleReview(index: number) {
    if (!reviews[index]?.body) return
    expandedReview = expandedReview === index ? null : index
  }

  /** Anilist-style review markup → HTML. */
  function formatReviewBody(body: string): string {
    if (!body) return ''
    const images: string[] = []
    let text = body.replace(/[Ii]mg(\d*)\(([^)]+)\)/g, (_, width, src) => {
      const i = images.length
      images.push(
        width
          ? `<img src="${src}" width="${width}" alt="" />`
          : `<img src="${src}" alt="" />`
      )
      return `\u0000IMG${i}\u0000`
    })
    text = text
      .replace(/\\n/g, '\n')
      .replace(/____([\s\S]*?)____/g, '<bold>$1</bold>')
      .replace(/__([\s\S]*?)__/g, '<bold>$1</bold>')
      .replace(/_([\s\S]*?)_/g, '<i>$1</i>')
      .replace(/\*\*([\s\S]*?)\*\*/g, '<bold>$1</bold>')
      .replace(/\*([\s\S]*?)\*/g, '<i>$1</i>')
      .replace(/~~~([\s\S]*?)~~~/g, '<div class="text-center">$1</div>')
      .replace(/~~([\s\S]*?)~~/g, '<s>$1</s>')
      .replace(/(^>\s?.*(?:\r?\n>\s?.*)*)/gm, (match) => {
        const content = match
          .split(/\r?\n/)
          .map((line) => line.replace(/^>\s?/, ''))
          .join('<br/>')
        return `<blockquote>${content}</blockquote>`
      })
      .replace(/---/g, '<hr/>')
      .replace(/\r\n|\r|\n/g, '<br/>')
    return text.replace(/\u0000IMG(\d+)\u0000/g, (_, i) => images[Number(i)] ?? '')
  }

  $effect(() => {
    void seriesid
    void currentTab
    expandedReview = null
    selectedIds = new Set()
    selectionAnchorId = null
  })

  let readingTab = $derived(currentTab === 'reviews' ? 'volume' : currentTab)
  let continueNum = $derived(books[readingTab]?.find((b: any) =>  !b.isCompleted )?.[readingTab] ?? 0 )
  let continueSubNum = $derived(books[readingTab]?.find((b: any) =>  !b.isCompleted )?.[readingTab == 'volume' ? 'subVolume' : 'subChapter'] ?? '0')

  const rowCount = $derived({
    volume: Math.ceil(books['volume']?.length / columns),
    chapter: Math.ceil(books['chapter']?.length / columns),
  })
  const rows = $derived({
    volume: Array.from({ length: rowCount['volume'] }, (_, i) => i),
    chapter: Array.from({ length: rowCount['chapter'] }, (_, i) => i),
  })

  const hasVolumes = $derived((books.volume?.length ?? 0) > 0)
  const hasChapters = $derived((books.chapter?.length ?? 0) > 0)
  const hasReviews = $derived((reviews?.length ?? 0) > 0)

  /** Show ~2½ VolChapSlot rows in the VList / reviews pane. */
  const listHeight = $derived(Math.max(120, itemHeight * 2.5))

  $effect(() => {
    const vol = hasVolumes
    const ch = hasChapters
    const rev = hasReviews
    const tab = untrack(() => currentTab)
    const ok =
      (tab === 'volume' && vol) ||
      (tab === 'chapter' && ch) ||
      (tab === 'reviews' && rev)
    if (ok) return
    if (vol) currentTab = 'volume'
    else if (ch) currentTab = 'chapter'
    else if (rev) currentTab = 'reviews'
  })

  // Keep maxWidth in sync with the list host (columns); height comes from itemHeight.
  $effect(() => {
    void winW
    void headerHeightRem
    void headerExpanded
    const el = listHostEl
    if (!el) return

    const sync = () => {
      maxWidth = el.clientWidth
    }
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    sync()
    return () => ro.disconnect()
  })

  // Columns from measured host width; min cover height in rem so it tracks root font-size.
  $effect(() => {
    if (!maxWidth) return
    const root = rootFontPx()
    const minH = MIN_COVER_REM * root
    const gap = gridGapRem * root
    let nextCols = 2
    let nextW = maxWidth
    for (let i = 10; i >= 1; i--) {
      const w = (maxWidth - gap * (i - 1)) / i
      if (w * COVER_ASPECT > minH) {
        nextCols = i
        nextW = w
        break
      }
    }
    columns = nextCols
    itemWidth = nextW
    itemHeight = nextW * COVER_ASPECT + TITLE_BAND_REM * root
  })

  $effect(() => {
    const v = series.Infos?.refreshedAt ? new Date(series.Infos.refreshedAt).getTime() : 0
    const bust = v ? `?v=${v}` : ''

    if (series.Status?.hasCover) {
      coverSrc = `${cdnUrl}/api/assets/cover/${seriesid}${bust}`
      if (cdnUrl.indexOf('http:') != -1) {
        const qs = [`upstream_addr=${cdnUrl.replace('http://', '')}`, v ? `v=${v}` : '']
          .filter(Boolean)
          .join('&')
        coverSrc = `https://proxy.nuread.direct.habysmol.com/api/assets/cover/${seriesid}?${qs}`
      }
    } else {
      coverSrc = PLACEHOLDER
    }

    if (series.Status?.hasBanner) {
      bannerSrc = `${cdnUrl}/api/assets/banner/${seriesid}${bust}`
      if (cdnUrl.indexOf('http:') != -1) {
        const qs = [`upstream_addr=${cdnUrl.replace('http://', '')}`, v ? `v=${v}` : '']
          .filter(Boolean)
          .join('&')
        bannerSrc = `https://proxy.nuread.direct.habysmol.com/api/assets/banner/${seriesid}?${qs}`
      }
    }
  })

  $inspect("progress:", progress)
  $inspect("books:", books)
</script>

<svelte:window bind:innerWidth={null, v => (winW = v!)} />

{#if series.localTitle}
  <div
    id="banner"
    class="mt-9 h-[380px] drop-shadow-2xl w-full bg-cover bg-center ..."
    style="background-image: url('{bannerSrc}')"
  />
  <div
    class="mt-10 h-[420px] absolute inset-0 pointer-events-none"
    style="background-image: {mode.current != 'dark' ?
      'linear-gradient(rgba(239, 241, 245, 0.47) 0%, rgb(220, 224, 232) 95%);' :
      'linear-gradient(rgba(36, 39, 58, 0.47) 0%, rgb(24, 25, 38) 95%);' }"
  />
  <div
    bind:this={headerShellEl}
    class="relative z-10 inset-x-0 bg-latte-crust dark:bg-macchiato-crust"
    style="height: {headerHeightRem}rem"
  >
    <div
      bind:this={headerMetaEl}
      class="absolute inset-x-0 ml-10 mt-0 md:-mt-40 z-1 rounded flex items-start"
    >
      <img
        class="h-52 sm:h-62 md:h-96 rounded object-cover shadow-2xl aspect-[2/3] bg-macchiato-base"
        src={coverSrc}
        alt={`${series?.localTitle ?? "series"} cover`}
        onerror={() => {
          if (coverSrc !== PLACEHOLDER) coverSrc = PLACEHOLDER
        }}
      />
      <div class="mx-4 md:mx-10 mt-2 md:mt-45 min-w-0 flex-1">
        <div
          bind:this={headerTitleEl}
          class={cn('text-xl mb-0', !headerExpanded && 'line-clamp-2')}
        >{series.localTitle}</div>
        <div class={cn(!headerExpanded && 'line-clamp-1 sm:line-clamp-8')}>
          {#each (series?.genres?.map(({ Name }) => Name) ?? []).slice(0, headerExpanded ? undefined : 8) as genre}
          <Badge
            class="py-0 font-medium text-latte-text dark:text-macchiato-text bg-latte-base dark:bg-macchiato-base m-0.5 overflow-hidden"
            ><Tags />{genre}</Badge
          >
          {/each}
        </div>
        {#if canSwitchDesc}
          <div
            class="mt-1.5 flex items-center gap-0.5"
            role="group"
            aria-label="Description language"
          >
            <button
              type="button"
              class={cn(
                'px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded transition-opacity',
                descLang === 'jp'
                  ? 'opacity-100 underline underline-offset-2'
                  : 'opacity-45 hover:opacity-80'
              )}
              aria-pressed={descLang === 'jp'}
              onclick={() => (descLang = 'jp')}
            >JP</button>
            <span class="text-[10px] opacity-30" aria-hidden="true">/</span>
            <button
              type="button"
              class={cn(
                'px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded transition-opacity',
                descLang === 'en'
                  ? 'opacity-100 underline underline-offset-2'
                  : 'opacity-45 hover:opacity-80',
                (enDescLoading || enDescError) && descLang !== 'en' && 'opacity-30'
              )}
              aria-pressed={descLang === 'en'}
              disabled={enDescLoading || (!enDescription && enDescError)}
              onclick={() => {
                if (enDescription) descLang = 'en'
              }}
            >{enDescLoading ? '…' : 'EN'}</button>
          </div>
        {/if}
        <div
          bind:this={headerDescEl}
          class={cn(
            'mt-2 opacity-80 font-light text-sm leading-snug',
            // Height budget in rem-friendly max-h; ellipsis from measured line-clamp.
            !headerExpanded && 'max-h-28 sm:max-h-36 md:max-h-44'
          )}
          style={headerExpanded ? '' : lineClampStyle(headerDescClamp)}
        >
          {@html displayDescription}
        </div>
        <div
          bind:this={headerTagsEl}
          class={cn('mt-2 flex flex-wrap', !headerExpanded && 'max-h-16 overflow-hidden')}
        >
          {#each (series?.tags?.map(({ Name }) => Name) ?? []) as tag}
          <Badge
            class="py-0 font-medium text-latte-text dark:text-macchiato-text bg-latte-base dark:bg-macchiato-base m-0.5 overflow-hidden"
            ><Hash />{tag}</Badge
          >
          {/each}
        </div>
        {#if showHeaderExpand}
          <Button
            variant="ghost"
            size="sm"
            class="mt-1 px-1 h-7 text-xs opacity-70 hover:opacity-100"
            onclick={() => (headerExpanded = !headerExpanded)}
          >
            {#if headerExpanded}
              <ChevronsDownUp />
              Show less
            {:else}
              <ChevronsUpDown />
              Show more
            {/if}
          </Button>
        {/if}
      </div>
      <div class="lg:min-w-85 pointer-events-none" />
    </div>

      <div
        bind:this={headerActionsEl}
        class="absolute z-1 left-12 top-[13.75rem] sm:top-[16.5rem] md:top-[15rem] flex flex-col md:flex-row md:items-center gap-2"
      >
        <Button variant="secondary" onclick={() => goto(`/media/${serverid}/library/${libraryid}/series/${seriesid}/${readingTab}/${continueNum}`)}>
          <Play />
          Continue  {readingTab == "volume" ? "Vol.":"Chp."} {continueNum}{continueSubNum ? '.' + continueSubNum : ''}
        </Button>
        <div class="flex items-center gap-2">
          <Button class="md:ml-3" variant="secondary">
            <Star />
          </Button>
          <Button variant="secondary">
            <Options />
          </Button>
        </div>
      </div>

    <SeriesInfos class="hidden lg:block absolute right-5 -top-20 z-10 w-80" {series} />
    <div class="absolute bottom-2 z-1 ml-10">
      <button
        disabled={!hasVolumes}
        onclick={() => {
          currentTab = 'volume'
        }}
        class="{currentTab == 'volume'
          ? activeTabHeaderClass
          : tabHeaderClass} mr-10 disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
      >Volumes</button
      >
      <button
        disabled={!hasChapters}
        onclick={() => {
          currentTab = 'chapter'
        }}
        class="{currentTab == 'chapter'
          ? activeTabHeaderClass
          : tabHeaderClass} mr-10 disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
      >Chapters</button
      >
      <span
        class="inline-block w-px h-4 ml-6 mr-16 align-middle bg-current opacity-30"
        aria-hidden="true"
      ></span>
      <button
        disabled={!hasReviews}
        onclick={() => {
          currentTab = 'reviews'
        }}
        class="{currentTab == 'reviews'
          ? activeTabHeaderClass
          : tabHeaderClass} mr-10 disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
      >Reviews</button
      >
    </div>
  </div>

  <SeriesInfos
    class="block lg:hidden mx-10 my-4"
    orientation="horizontal"
    {series}
  />

  <div class="flex pl-10" style="height: {listHeight}px">
    <div bind:this={listHostEl} class="min-w-0 flex-1 h-full">
    {#if currentTab === 'reviews'}
      <div
        class="overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-latte-surface1 scrollbar-track-latte-mantle dark:scrollbar-thumb-macchiato-surface1 dark:scrollbar-track-macchiato-crust"
        style={`height: ${listHeight}px;`}
      >
        {#if reviews.length === 0}
          <p class="mt-6 text-sm opacity-60 font-light">No reviews yet.</p>
        {:else}
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 py-4 pr-2">
            {#each reviews as review, i (i)}
              {@const open = expandedReview === i}
              {@const expandable = !!review.body}
              <Card.Root
                class={cn(
                  'gap-0 py-0 overflow-hidden transition-colors h-fit',
                  'bg-latte-base dark:bg-macchiato-base border-latte-overlay0/30 dark:border-macchiato-overlay0/30',
                  expandable && 'cursor-pointer',
                  open && 'border-latte-overlay1 dark:border-macchiato-overlay1'
                )}
              >
                <button
                  type="button"
                  class="w-full text-left px-5 py-4 flex items-start gap-3 disabled:cursor-default"
                  onclick={() => toggleReview(i)}
                  aria-expanded={open}
                  disabled={!expandable}
                >
                  <div class="flex items-center gap-1.5 shrink-0 mt-0.5 min-w-14">
                    <Star class="size-4 fill-current opacity-80" />
                    <span class="text-sm font-medium tabular-nums">
                      {review.score ?? '—'}
                    </span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class={cn('text-sm font-medium', !open && 'line-clamp-2')}>
                      {review.summary || 'Untitled review'}
                    </p>
                  </div>
                  {#if expandable}
                    <ChevronDown
                      class={cn(
                        'size-4 shrink-0 mt-0.5 opacity-50 transition-transform',
                        open && 'rotate-180'
                      )}
                    />
                  {/if}
                </button>
                {#if open}
                  <Card.Content class="px-5 pb-5 pt-0">
                    <div
                      class="text-sm opacity-80 font-light leading-relaxed border-t border-latte-overlay0/30 dark:border-macchiato-overlay0/30 pt-4 [&_p]:mb-3 [&_br]:leading-loose [&_bold]:font-bold [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_hr]:my-4 [&_hr]:border-current [&_s]:line-through [&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-current [&_blockquote]:pl-3 [&_blockquote]:opacity-90"
                    >
                      {@html formatReviewBody(review.body)}
                    </div>
                  </Card.Content>
                {/if}
              </Card.Root>
            {/each}
          </div>
        {/if}
      </div>
    {:else}
      {#key `${currentTab}-${columns}`}
      <VList
        bufferSize={itemHeight * 0.6}
        data={rows[currentTab]}
        style={`height: ${listHeight}px;`}
        class="scrollbar-thin scrollbar-thumb-latte-surface1 scrollbar-track-latte-mantle dark:scrollbar-thumb-macchiato-surface1 dark:scrollbar-track-macchiato-crust px-2"
        getKey={row => row}
      >
        {#snippet children(rowIndex)}
          <div
            class="grid my-2"
            style={`grid-template-columns: repeat(${columns}, minmax(0, 1fr)); gap: ${gridGapRem}rem;`}
          >
            {#each books[currentTab].slice(rowIndex * columns, (rowIndex + 1) * columns) as book (book.id)}
              <VolChapSlot
                width={itemWidth}
                {book}
                {seriesid}
                {serverid}
                {libraryid}
                currentPage={book?.progression ?? 0}
                isCompleted={book?.isCompleted}
                selected={isSlotSelected(book)}
                {selectionActive}
                onToggleSelect={toggleBookSelect}
              />
            {/each}
          </div>
        {/snippet}
      </VList>
      {/key}
    {/if}
    </div>
    <div class="lg:min-w-85 pointer-events-none" />
  </div>

  {#if selectionActive && currentTab !== 'reviews'}
    <div
      class="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 flex-wrap items-center gap-2 rounded-lg border border-latte-surface1/60 bg-latte-mantle/90 px-4 py-3 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.35)] backdrop-blur-md dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/90"
      role="toolbar"
      aria-label="Book selection actions"
    >
      <span class="text-sm font-medium tabular-nums mr-1">
        {selectedIds.size} selected
      </span>
      <Button
        variant="secondary"
        size="sm"
        disabled={markingBusy}
        onclick={() => markSelection(true)}
      >
        <BookCheck />
        Mark as Read
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={markingBusy}
        onclick={() => markSelection(false)}
      >
        <BookX />
        Mark as Unread
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={markingBusy}
        onclick={clearSelection}
        aria-label="Clear selection"
      >
        <X />
        Clear
      </Button>
    </div>
  {/if}
{/if}
